import React, { useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { ExportButton } from "../../Components/ExportButton";
import { GetSubscriptionReports } from "../../Api/subscription";
import SubscriptionHistoryTab from "./components/SubscriptionHistoryTab";
import PaidUserTab from "./components/PaidUserTab";
import CancelledUserTab from "./components/CancelledUserTab";
import FreePaidChartTab from "./components/FreePaidChartTab";
import FilterSidebar from "../../Components/FilterSidebar";
import Pagination from "../../Utils/Pagination";
import { FaPoundSign, FaCalendarAlt, FaCalendarCheck, FaUserEdit } from "react-icons/fa";

const Subscription = () => {
  const [type, setType] = useState("report");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    preset: "all",
    startDate: null,
    endDate: null,
    planType: "all",
    userId: null,
    userOption: null,
  });
  const defaultFilters = useRef({
    search: "",
    preset: "all",
    startDate: null,
    endDate: null,
    planType: "all",
    userId: null,
    userOption: null,
  });
  const [reportPage, setReportPage] = useState(0);
  const [reportRowsPerPage, setReportRowsPerPage] = useState(10);
  const [reportTotalCount, setReportTotalCount] = useState(0);
  const [reportTotals, setReportTotals] = useState({
    totalAmount: 0,
    monthlyAmount: 0,
    yearlyAmount: 0,
    manualAmount: 0,
  });

  // User tab pagination
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(10);
  const [userTotalCount, setUserTotalCount] = useState(0);

  // Cancelled tab pagination
  const [cancelledPage, setCancelledPage] = useState(0);
  const [cancelledRowsPerPage, setCancelledRowsPerPage] = useState(10);
  const [cancelledTotalCount, setCancelledTotalCount] = useState(0);

  const handleApplyFilters = (values) => {
    setFilters(values);
  };

  const handleResetFilters = () => {
    setFilters({
      search: "",
      preset: "all",
      startDate: null,
      endDate: null,
      planType: "all",
      userId: null,
      userOption: null,
    });
  };

  // Report tab handlers
  const handleChangeReportPage = (_event, newPage) => {
    setReportPage(newPage);
  };

  const handleChangeReportRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    setReportRowsPerPage(next);
    setReportPage(0);
  };

  // User tab handlers
  const handleChangeUserPage = (_event, newPage) => {
    setUserPage(newPage);
  };

  const handleChangeUserRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    setUserRowsPerPage(next);
    setUserPage(0);
  };

  // Cancelled tab handlers
  const handleChangeCancelledPage = (_event, newPage) => {
    setCancelledPage(newPage);
  };

  const handleChangeCancelledRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    setCancelledRowsPerPage(next);
    setCancelledPage(0);
  };

  const handleReportLoaded = useCallback(({ total, totals, page }) => {
    setReportTotalCount(total);
    console.log(reportPage, "reportPage");
    console.log(totals, "totals");
    if (totals && page == 1) {
      setReportTotals({
        totalAmount: totals.totalAmount ?? 0,
        monthlyAmount: totals.monthlyAmount ?? 0,
        yearlyAmount: totals.yearlyAmount ?? 0,
        manualAmount: totals.manualAmount ?? 0,
      });
    }
  }, []);

  const handleUserLoaded = useCallback(({ total, totals }) => {
    setUserTotalCount(total);
  }, []);

  const handleCancelledLoaded = useCallback(({ total, totals }) => {
    setCancelledTotalCount(total);
  }, []);

  return (
    <>
      <Helmet>
        <title>Ove | Admin Subscription</title>
      </Helmet>
      <div className="page-wrapper subscription-container">
        <div className="content container-fluid">
          <div className="admin-wrapper subscription-table">
            <div className="subscription-tabs mb-3" style={{ display: "flex", flexWrap: "wrap", borderBottom: "none" }}>
              <button
                type="button"
                className={`subscription-tab-btn ${type === "report" ? "active" : ""}`}
                onClick={() => {
                  if (type !== "report") {
                    setType("report");
                    setFilters(defaultFilters.current);
                    setReportPage(0);
                    setUserPage(0);
                    setCancelledPage(0);
                    setIsFilterOpen(false);
                  }
                }}
              >
                Subscription history
              </button>
              <button
                type="button"
                className={`subscription-tab-btn ${type === "user" ? "active" : ""}`}
                onClick={() => {
                  if (type !== "user") {
                    setType("user");
                    setFilters(defaultFilters.current);
                    setReportPage(0);
                    setUserPage(0);
                    setCancelledPage(0);
                    setIsFilterOpen(false);
                  }
                }}
              >
                Paid users
              </button>
              <button
                type="button"
                className={`subscription-tab-btn ${type === "cancelled" ? "active" : ""}`}
                onClick={() => {
                  if (type !== "cancelled") {
                    setType("cancelled");
                    setFilters(defaultFilters.current);
                    setReportPage(0);
                    setUserPage(0);
                    setCancelledPage(0);
                    setIsFilterOpen(false);
                  }
                }}
              >
                Cancelled users
              </button>
              <button
                type="button"
                className={`subscription-tab-btn ${type === "free-paid-chart" ? "active" : ""}`}
                onClick={() => {
                  if (type !== "free-paid-chart") {
                    setType("free-paid-chart");
                    setFilters(defaultFilters.current);
                    setReportPage(0);
                    setUserPage(0);
                    setCancelledPage(0);
                    setIsFilterOpen(false);
                  }
                }}
              >
                Free vs Paid users
              </button>
            </div>

            {type === "report" && (
              <div className="row subscription-stats-row">
                <div className="col-xl-3 col-lg-6 mb-3">
                  <div className="subscription-stats-card">
                    <div className="subscription-stats-content">
                      <div className="subscription-stats-title">Total Amount</div>
                      <div className="subscription-stats-value">£{reportTotals.totalAmount}</div>
                    </div>
                    <div className="subscription-stats-icon">
                      <FaPoundSign />
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 mb-3">
                  <div className="subscription-stats-card">
                    <div className="subscription-stats-content">
                      <div className="subscription-stats-title">Monthly</div>
                      <div className="subscription-stats-value">£{reportTotals.monthlyAmount}</div>
                    </div>
                    <div className="subscription-stats-icon">
                      <FaCalendarAlt />
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 mb-3">
                  <div className="subscription-stats-card">
                    <div className="subscription-stats-content ">
                      <div className="subscription-stats-title">Yearly</div>
                      <div className="subscription-stats-value">£{reportTotals.yearlyAmount}</div>
                    </div>
                    <div className="subscription-stats-icon">
                      <FaCalendarCheck />
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-lg-6 mb-3">
                  <div className="subscription-stats-card">
                    <div className="subscription-stats-content">
                      <div className="subscription-stats-title">Manual</div>
                      <div className="subscription-stats-value">£{reportTotals.manualAmount}</div>
                    </div>
                    <div className="subscription-stats-icon">
                      <FaUserEdit />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-12">
                <div>
                  <div className="subscription-table-header">
                    <div className="subscription-table-actions">
                      <div className="subscription-action-buttons">
                        {type === "report" && (
                          <ExportButton
                            moduleName="subscription-report"
                            apiService={() => GetSubscriptionReports({ page: 1, limit: 10, isExport: true })}
                            data={{}}
                            className="primary-button action-btn"
                            disabled={reportTotalCount === 0}
                          />
                        )}
                        {type !== "free-paid-chart" && (
                          <button
                            type="button"
                            className={`subscription-filter-trigger action-btn ${isFilterOpen ? 'active' : ''}`}
                            onClick={() => setIsFilterOpen(true)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                              <line x1="4" y1="6" x2="20" y2="6"></line>
                              <line x1="4" y1="12" x2="20" y2="12"></line>
                              <line x1="4" y1="18" x2="20" y2="18"></line>
                            </svg>
                            Filters
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    {type === "report" && (
                      <>
                        <SubscriptionHistoryTab
                          filters={filters}
                          page={reportPage}
                          rowsPerPage={reportRowsPerPage}
                          onLoaded={handleReportLoaded}
                        />
                        <Pagination
                          totalItem={reportTotalCount}
                          itemsPerPage={reportRowsPerPage}
                          page={reportPage}
                          handleChangePage={handleChangeReportPage}
                          handleChangeRowsPerPage={handleChangeReportRowsPerPage}
                        />
                      </>
                    )}
                    {type === "user" && (
                      <>
                        <PaidUserTab
                          filters={filters}
                          page={userPage}
                          rowsPerPage={userRowsPerPage}
                          onLoaded={handleUserLoaded}
                        />
                        <Pagination
                          totalItem={userTotalCount}
                          itemsPerPage={userRowsPerPage}
                          page={userPage}
                          handleChangePage={handleChangeUserPage}
                          handleChangeRowsPerPage={handleChangeUserRowsPerPage}
                        />
                      </>
                    )}
                    {type === "cancelled" && (
                      <>
                        <CancelledUserTab
                          filters={filters}
                          page={cancelledPage}
                          rowsPerPage={cancelledRowsPerPage}
                          onLoaded={handleCancelledLoaded}
                        />
                        <Pagination
                          totalItem={cancelledTotalCount}
                          itemsPerPage={cancelledRowsPerPage}
                          page={cancelledPage}
                          handleChangePage={handleChangeCancelledPage}
                          handleChangeRowsPerPage={handleChangeCancelledRowsPerPage}
                        />
                      </>
                    )}
                    {type === "free-paid-chart" && (
                      <FreePaidChartTab
                        onLoaded={() => { }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <FilterSidebar
              open={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              initialFilters={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              moduleType="subscription"
              setPage={() => { setReportPage(0); setUserPage(0); setCancelledPage(0); }}
              tabType={type}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Subscription;

